<script lang="ts" setup>
import { ref, computed, type PropType, type Ref } from 'vue';
import BButton from '@/components/common/bButton.vue';
import BSelect from '@/components/common/bSelect.vue';
import BLabel from '@/components/common/bLabel.vue';
import BExpandablePill from '@/components/common/BExpandablePill.vue';
import Loading from '@/components/common/loading.vue';
import Burger from '@/components/navigationDrawer/burger.vue';
import DefaultProfile from '@/assets/svg/profile.svg';
import SolarSettingsBroken from '~icons/solar/settings-broken';
import SolarKeyBroken from '~icons/solar/key-broken';
import SolarUserCircleBroken from '~icons/solar/user-circle-broken'
import MaterialSymbolsLockOutline from '~icons/material-symbols/lock-outline';
import MaterialSymbolsLockOpenOutlineRounded from '~icons/material-symbols/lock-open-outline-rounded';
import PhSunDuotone from '~icons/ph/sun-duotone';
import PhMoonStarsDuotone from '~icons/ph/moon-stars-duotone';
import CircleFlagsFr from '~icons/circle-flags/fr';
import CircleFlagsLangEn from '~icons/circle-flags/lang-en';
import { useDark } from '@vueuse/core';
import { useUserStore } from '@/config/store/userStore';
import { userApi } from '@/services/api';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import SearchCommand from '@/components/application/searchCommand/SearchCommand.vue'
import MaterialSymbolsMailOutlineRounded from '~icons/material-symbols/mail-outline-rounded'
import MaterialSymbolsChatOutlineRounded from '~icons/material-symbols/chat-outline-rounded'
import { useChatStore } from '@/config/store/chatStore'

const userstore = useUserStore()
const chatStore = useChatStore()
const { t, locale } = useI18n();
const router = useRouter();
const isDark = useDark();

const openMobileChat = () => {
  if (chatStore.widgetHidden) {
    chatStore.showWidget()
  }
  chatStore.openWidget()
}

const changeLocale = () => {
  locale.value = locale.value === 'fr' ? 'en' : 'fr'
  if (userstore.user.user) {
    userApi.updateLocale(locale.value.toUpperCase())
    userstore.user.user.lang = locale.value.toUpperCase()
  }
}

const toggleTheme = () => {
  isDark.value = !isDark.value
}

const props = defineProps({
  user: {
    type: Object as PropType<User | null>,
    required: true,
  },
  availableProfiles: {
    type: Array as PropType<Profile[]>,
    default: () => [],
  },
  currentProfile: {
    type: Object,
    default: null,
  },
  isUpdatingProfile: {
    type: Boolean,
    default: false,
  },
  isFixed: {
    type: Boolean,
    required: true,
  },
  isOpen: { // Mobile open state
    type: Boolean,
    required: true,
  },
  isHovering: { // Is the parent drawer being hovered (when not fixed)?
    type: Boolean,
    required: true,
  },
  drawerRef: { // Needed for BSelect popover positioning
    type: Object as PropType<HTMLElement | null>,
    default: null,
  },
  // Functions passed down from the main drawer
  updateProfileFn: {
    type: Function as PropType<(profile) => void>,
    required: true,
  },
  toggleMobileMenuFn: {
    type: Function as PropType<() => void>,
    required: true,
  },
  toggleFixedFn: {
    type: Function as PropType<() => void>,
    required: true,
  },
  emitChangePasswordFn: {
     type: Function as PropType<() => void>,
     required: true,
  },
  canAccessInbox: {
    type: Boolean,
    default: false,
  },
  inboxUnreadCount: {
    type: Number,
    default: 0,
  },
  canAccessChat: {
    type: Boolean,
    default: false,
  },
  chatUnreadCount: {
    type: Number,
    default: 0,
  },
});

const imageError = ref(false);
const showSelect = ref(false);

const isAdminUser = computed(() => props.user?.role === 'admin' && !props.user?._firmId);

const adminDisplayName = computed(() => {
  if (!props.user) return 'Admin'
  const name = `${props.user.firstname || ''} ${props.user.lastname || ''}`.trim()
  return name || props.user.email || 'Admin'
})

// Determines if the text/details should be shown based on fixed/hover state
const showDetails = computed(() => props.isFixed || props.isHovering || props.isOpen);

const canSwitchProfile = computed(() => props.availableProfiles.length > 1);

const handleProfileUpdate = (profile) => {
  showSelect.value = false;
  if (profile._id !== props.currentProfile?._id) {
    
    props.updateProfileFn(profile); // Call the function passed from parent
  }
};


const expandableItems = computed(() => {
  const items = []

  if (!isAdminUser.value && userstore.user?._id) {
    items.push({
      _id: 'profile',
      icon: SolarUserCircleBroken,
      title: t('components.navigationDrawer.drawerHeader.profile'),
      onClick: () => router.push({ name: 'EmployeeProfile', params: { resourceId: userstore.user._id } }),
    })
  }

  items.push(
    {
      _id: 'change-password',
      icon: SolarKeyBroken,
      title: t('components.navigationDrawer.drawerHeader.changePassword'),
      onClick: () => props.emitChangePasswordFn(),
    },
    {
      _id: 'fix-menu',
      icon: props.isFixed ? MaterialSymbolsLockOpenOutlineRounded : MaterialSymbolsLockOutline,
      title: props.isFixed ? t('components.navigationDrawer.drawerHeader.collapseMenu') : t('components.navigationDrawer.drawerHeader.fixMenu'),
      onClick: () => props.toggleFixedFn(),
    },
    {
      _id: 'change-language',
      icon: locale.value === 'fr' ? CircleFlagsFr : CircleFlagsLangEn,
      title: t('components.navigationDrawer.drawerHeader.changeLanguage'),
      onClick: changeLocale,
    },
    {
      _id: 'change-color-theme',
      icon: isDark.value ? PhMoonStarsDuotone : PhSunDuotone,
      title: t('components.navigationDrawer.drawerHeader.changeColorTheme'),
      onClick: toggleTheme,
    },
  )

  return items
});

</script>

<template>
  <div v-if="user" class="flex items-center pl-2 h-16 md:h-24 relative justify-between md:justify-start shrink-0">
    <!-- Avatar -->
    <router-link
      v-if="user._id && !isAdminUser"
      :to="{ name: 'EmployeeProfile', params: { resourceId: user._id } }"
      class="rounded-full border-2 border-blue-500 mr-3 overflow-hidden md:h-10 md:w-10 w-8 h-8 aspect-square shrink-0 cursor-pointer hover:border-blue-600 transition-colors"
      :title="`${user.firstname} ${user.lastname}`"
    >
      <img
        v-if="user.picture && !imageError"
        width="40" height="40" loading="lazy"
        :src="user.picture"
        :alt="`Avatar de ${user.firstname} ${user.lastname}`"
        class="object-cover h-full w-full"
        @error="() => imageError = true"
      >
      <DefaultProfile v-else class="h-full w-full" />
    </router-link>
    <div
      v-else
      class="rounded-full border-2 border-warning-500 mr-3 overflow-hidden md:h-10 md:w-10 w-8 h-8 aspect-square shrink-0"
      :title="adminDisplayName"
    >
      <img
        v-if="user.picture && !imageError"
        width="40" height="40" loading="lazy"
        :src="user.picture"
        :alt="adminDisplayName"
        class="object-cover h-full w-full"
        @error="() => imageError = true"
      >
      <DefaultProfile v-else class="h-full w-full" />
    </div>

    <!-- Profile Info & Actions (Show on Hover/Fixed/Mobile Open) -->
    <div
      v-show="showDetails"
      class="menu-item-show-hover flex-col px-2 gap-2 items-start w-full overflow-hidden"
      :class="{ 'md:opacity-100 md:flex': isFixed, 'md:opacity-0 md:hidden': !isFixed }"
    >
      <BLabel
        v-if="isAdminUser"
        size="dense"
        bg-color="warning-op"
      >
        {{ adminDisplayName }}
      </BLabel>
      <!-- Profile Selector -->
      <BSelect
        v-else-if="canSwitchProfile && showSelect && currentProfile"
        :popover-container="drawerRef"
        class="w-full shrink-0 min-w-20 grow flex-1"
        size="dense"
        :options="availableProfiles"
        display-prop="display"
        value-prop="_id"
        :model-value="currentProfile"
        @update:model-value="handleProfileUpdate"
      />
      <!-- Profile Display -->
      <BLabel
        v-else-if="currentProfile"
        size="dense"
        bg-color="primary-op"
        :class="{ 'cursor-pointer hover:brightness-110': canSwitchProfile }"
        :title="canSwitchProfile ? t('components.navigationDrawer.drawerHeader.switchProfile') : currentProfile.display"
        @click="showSelect = canSwitchProfile ? true : false"
      >
        <template v-if="!isUpdatingProfile">
          {{ currentProfile.display }}
        </template>
        <Loading v-else size="sm"/>
      </BLabel>

      <!-- Desktop Settings Expandable Pill -->
      <div class="hidden md:flex gap-2 text-xl items-center">
        <BExpandablePill
            :label="t('components.navigationDrawer.drawerHeader.accountSettings')"
            :items="expandableItems"
            bg="base"
            size="dense"
            :disabled="isUpdatingProfile"
        >
          <template #button>
            <span class="flex items-center gap-1 whitespace-nowrap" data-cy="settings-dropdown">
              <SolarSettingsBroken /> {{ t('components.navigationDrawer.drawerHeader.accountSettings') }}
            </span>
          </template>
        </BExpandablePill>
      </div>
    </div>

    <div class="md:hidden flex items-center gap-2 ml-auto">
      
        <SearchCommand/>

        <!-- Mobile Chat Button — opens floating overlay (full page via expand inside widget) -->
        <button
          v-if="canAccessChat"
          type="button"
          class="relative p-2 rounded-lg bg-hover"
          data-cy="chat-mobile-header-button"
          :title="t('common.drawer.messaging')"
          @click="openMobileChat"
        >
          <MaterialSymbolsChatOutlineRounded class="w-5 h-5" />
          <span
            v-if="chatUnreadCount > 0"
            class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1"
            data-cy="chat-mobile-header-unread"
          >
            {{ chatUnreadCount > 99 ? '99+' : chatUnreadCount }}
          </span>
        </button>

        <!-- Mobile Inbox Button -->
        <router-link
          v-if="canAccessInbox"
          :to="{ name: 'Inbox' }"
          class="relative p-2 rounded-lg bg-hover"
          :title="t('common.drawer.inbox')"
        >
          <MaterialSymbolsMailOutlineRounded class="w-5 h-5" />
          <span 
            v-if="inboxUnreadCount > 0" 
            class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-5 h-5 flex items-center justify-center px-1"
          >
            {{ inboxUnreadCount > 99 ? '99+' : inboxUnreadCount }}
          </span>
        </router-link>
        
        <!-- Mobile Burger -->
        <Burger class="ml-auto mr-4 md:hidden" :is-open="isOpen" @toggle="toggleMobileMenuFn" />
      </div>
  </div>
</template>

<style scoped>
/* Styles for showing details on hover/fixed state */
.drawer-container:is(:hover, :has(:focus-visible)) .menu-item-show-hover,
.drawer-container-fixed .menu-item-show-hover {
  opacity: 1 !important;
  display: flex !important;
  transition: opacity 0.3s ease-in-out;
}
/* Ensure initial state is hidden correctly on desktop when not fixed */
@media (min-width: 768px) {
  .menu-item-show-hover:not(.md\:flex) {
     opacity: 0;
     display: none;
   }
}
</style>
